import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Snackbar,
  IconButton
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { schoolsApi } from '../../utils/apiService';

const GRADE_ORDER = ['KG', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];

const EnrollmentManagementModal = ({ selectedSchool, onEnrollmentChange, userPermissions }) => {
  const [enrollmentData, setEnrollmentData] = useState({});
  const [gradesOffered, setGradesOffered] = useState({});
  const [dataProvenance, setDataProvenance] = useState({});
  const [gradeDisplayMap, setGradeDisplayMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editingData, setEditingData] = useState({});
  const [deletedItems, setDeletedItems] = useState({ years: [], grades: {} }); // Track deletions
  const [validationResults, setValidationResults] = useState([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [deleteConfirmAction, setDeleteConfirmAction] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  // const [validationConfig, setValidationConfig] = useState({});
  // const [pendingData, setPendingData] = useState({});

  // Load enrollment management data
  const loadData = useCallback(async () => {
    if (!selectedSchool?.uuid) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await schoolsApi.getEnrollmentManagementData(selectedSchool.uuid);
      
      setEnrollmentData(data.enrollment_data || {});
      setGradesOffered(data.grades_offered || {});
      setDataProvenance(data.data_provenance || {});
      setGradeDisplayMap(data.grade_display_map || {});
      // setValidationConfig(data.validation_config || {});
      
    } catch (error) {
      console.error('Error loading enrollment management data:', error);
      setError(error.message || 'Failed to load enrollment data');
    } finally {
      setLoading(false);
    }
  }, [selectedSchool?.uuid]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Get available years sorted by most recent first
  const getAvailableYears = useCallback(() => {
    const years = Object.keys(enrollmentData).sort((a, b) => {
      const yearA = parseInt(a.split('-')[0]);
      const yearB = parseInt(b.split('-')[0]);
      return yearB - yearA; // Most recent first
    });
    return years;
  }, [enrollmentData]);

  // Get grades for a specific year
  const getGradesForYear = useCallback((year) => {
    // Check if this is a newly added year (exists in editingData but not enrollmentData)
    const isNewYear = editingData[year] && !enrollmentData[year];
    
    if (isNewYear) {
      // For new years, always use grades from the most recent historical year
      const availableYears = Object.keys(enrollmentData).sort((a, b) => {
        const yearA = parseInt(a.split('-')[0]);
        const yearB = parseInt(b.split('-')[0]);
        return yearB - yearA;
      });
      
      if (availableYears.length > 0) {
        const latestYear = availableYears[0];
        const latestDataYear = parseInt(latestYear.split('-')[0]);
        const latestOffered = gradesOffered[latestDataYear] || [];
        const latestEnrollmentGrades = Object.keys(enrollmentData[latestYear] || {});
        const latestAllGrades = new Set([...latestOffered, ...latestEnrollmentGrades]);
        const gradesFromLatest = GRADE_ORDER.filter(grade => latestAllGrades.has(grade));
        
        if (gradesFromLatest.length > 0) {
          return gradesFromLatest;
        }
      }
      
      // Fallback for new years with no historical data
      return ['KG', '01', '02', '03', '04', '05'];
    }
    
    // For existing years, use the normal logic
    const dataYear = parseInt(year.split('-')[0]);
    const offered = gradesOffered[dataYear] || [];
    const enrollmentGrades = Object.keys(enrollmentData[year] || {});
    const editingGrades = Object.keys(editingData[year] || {});
    
    const allGrades = new Set([...offered, ...enrollmentGrades, ...editingGrades]);
    let availableGrades = GRADE_ORDER.filter(grade => allGrades.has(grade));
    
    // If no grades found for existing year, try to get from most recent year
    if (availableGrades.length === 0) {
      const availableYears = Object.keys(enrollmentData).sort((a, b) => {
        const yearA = parseInt(a.split('-')[0]);
        const yearB = parseInt(b.split('-')[0]);
        return yearB - yearA;
      });
      
      if (availableYears.length > 0) {
        const latestYear = availableYears[0];
        const latestDataYear = parseInt(latestYear.split('-')[0]);
        const latestOffered = gradesOffered[latestDataYear] || [];
        const latestEnrollmentGrades = Object.keys(enrollmentData[latestYear] || {});
        const latestAllGrades = new Set([...latestOffered, ...latestEnrollmentGrades]);
        availableGrades = GRADE_ORDER.filter(grade => latestAllGrades.has(grade));
      }
      
      // Final fallback
      if (availableGrades.length === 0) {
        availableGrades = ['KG', '01', '02', '03', '04', '05'];
      }
    }
    
    return availableGrades;
  }, [gradesOffered, enrollmentData, editingData]);

  // Start editing mode
  const handleEditMode = () => {
    setEditMode(true);
    setEditingData(JSON.parse(JSON.stringify(enrollmentData)));
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditMode(false);
    setEditingData({});
    setDeletedItems({ years: [], grades: {} }); // Reset deletion tracking
    setValidationResults([]);
  };

  // Update enrollment value during editing
  const handleEnrollmentChange = (year, grade, value) => {
    const numValue = value === '' ? 0 : parseInt(value) || 0;
    
    setEditingData(prev => ({
      ...prev,
      [year]: {
        ...prev[year],
        [grade]: {
          ...prev[year]?.[grade],
          total: numValue
        }
      }
    }));
  };

  // Add a new year for data entry
  const handleAddYear = () => {
    // Find the latest year and add 1 to it
    const availableYears = getAvailableYears();
    let latestYear;
    
    if (availableYears.length > 0) {
      // Get the latest year from existing data
      const latestYearKey = availableYears[0]; // Already sorted desc
      latestYear = parseInt(latestYearKey.split('-')[0]);
    } else {
      // No existing data, use current year
      latestYear = new Date().getFullYear();
    }
    
    const newStartYear = latestYear + 1;
    const newEndYear = newStartYear + 1;
    const newYearKey = `${newStartYear}-${newEndYear}`;
    
    // Enter edit mode if not already in it
    if (!editMode) {
      const newEditingData = JSON.parse(JSON.stringify(enrollmentData));
      newEditingData[newYearKey] = {};
      setEditingData(newEditingData);
      setEditMode(true);
    } else {
      // Already in edit mode, just add the year
      if (!editingData[newYearKey]) {
        setEditingData(prev => ({
          ...prev,
          [newYearKey]: {}
        }));
      }
    }
  };

  // Delete a year with confirmation
  const handleDeleteYear = (yearToDelete) => {
    setDeleteConfirmAction({
      type: 'year',
      year: yearToDelete,
      message: `Are you sure you want to delete all enrollment data for ${yearToDelete}? This action cannot be undone.`
    });
    setShowDeleteConfirmDialog(true);
  };

  // Confirm deletion action
  const handleConfirmDelete = () => {
    const { type, year } = deleteConfirmAction;
    
    if (type === 'year') {
      // Track the year for deletion
      setDeletedItems(prev => ({
        ...prev,
        years: [...prev.years, year]
      }));
      
      // Remove from editing data
      if (!editMode) {
        const newEditingData = JSON.parse(JSON.stringify(enrollmentData));
        delete newEditingData[year];
        setEditingData(newEditingData);
        setEditMode(true);
      } else {
        setEditingData(prev => {
          const newData = { ...prev };
          delete newData[year];
          return newData;
        });
      }
    }
    
    setShowDeleteConfirmDialog(false);
    setDeleteConfirmAction(null);
  };

  // Cancel deletion
  const handleCancelDelete = () => {
    setShowDeleteConfirmDialog(false);
    setDeleteConfirmAction(null);
  };

  // Validate enrollment data
  const validateData = async (data) => {
    try {
      const response = await schoolsApi.validateEnrollmentData(selectedSchool.uuid, { enrollment_data: data });
      return response.results || [];
    } catch (error) {
      console.error('Error validating data:', error);
      return [];
    }
  };

  // Save enrollment data (handles both updates and deletions)
  const handleSave = async (forceOverride = false) => {
    setSaveLoading(true);
    
    try {
      // First handle deletions if any
      if (deletedItems.years.length > 0 || Object.keys(deletedItems.grades).length > 0) {
        await schoolsApi.deleteEnrollmentData(selectedSchool.uuid, {
          delete_years: deletedItems.years,
          delete_grades: deletedItems.grades
        });
      }

      // Then handle updates/inserts
      const dataToSave = {};
      Object.entries(editingData).forEach(([year, grades]) => {
        dataToSave[year] = {};
        Object.entries(grades).forEach(([grade, gradeData]) => {
          if (gradeData.total !== undefined) {
            dataToSave[year][grade] = {
              total: gradeData.total,
              demographics: {} // TODO: Add demographics support
            };
          }
        });
      });

      // Only save if there's data to save
      if (Object.keys(dataToSave).length > 0) {
        // Validate first if not overriding
        if (!forceOverride) {
          const validationRes = await validateData(dataToSave);
          const errors = (validationRes.results || validationRes).filter(v => v.level === 'error');
          
          if (errors.length > 0) {
            setValidationResults(validationRes.results || validationRes);
            // setPendingData(dataToSave);
            setShowValidationDialog(true);
            setSaveLoading(false);
            return;
          }
        }

        // Save the data
        await schoolsApi.saveEnrollmentData(selectedSchool.uuid, {
          enrollment_data: dataToSave,
          validation_override: forceOverride
        });
      }

      // Success - reload data and exit edit mode
      await loadData();
      setEditMode(false);
      setEditingData({});
      setDeletedItems({ years: [], grades: {} }); // Reset deletion tracking
      setValidationResults([]);
      setSuccessMessage('Enrollment data saved successfully!');
      
      // Notify parent component of changes and wait for completion
      console.log('[EnrollmentManagementModal] Calling onEnrollmentChange callback...');
      if (onEnrollmentChange) {
        await onEnrollmentChange();
        console.log('[EnrollmentManagementModal] onEnrollmentChange callback completed');
      } else {
        console.warn('[EnrollmentManagementModal] No onEnrollmentChange callback provided!');
      }

    } catch (error) {
      console.error('Error saving enrollment data:', error);
      setError(error.message || 'Failed to save enrollment data');
    } finally {
      setSaveLoading(false);
    }
  };

  // Force save with validation override
  const handleForceSave = () => {
    setShowValidationDialog(false);
    handleSave(true);
  };

  // Get status indicator for a data point
  const getStatusIndicator = (year, grade) => {
    const provenance = dataProvenance[year]?.[grade];
    if (!provenance) return null;

    if (provenance.is_user_edited) {
      return <Chip size="small" label="User" color="primary" icon={<EditIcon />} />;
    } else {
      return <Chip size="small" label="Official" color="success" icon={<CheckCircleIcon />} />;
    }
  };

  // Render enrollment grid
  const renderEnrollmentGrid = () => {
    const years = editMode ? Object.keys(editingData) : getAvailableYears();
    const currentData = editMode ? editingData : enrollmentData;

    if (years.length === 0) {
      return (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary">
            No enrollment data available. 
            {userPermissions.canEdit && (
              <> Click "Add Data" to start entering enrollment information.</>
            )}
          </Typography>
        </Box>
      );
    }

    // Sort years with most recent on the right
    const sortedYears = [...years].sort((a, b) => {
      const yearA = parseInt(a.split('-')[0]);
      const yearB = parseInt(b.split('-')[0]);
      return yearA - yearB; // Ascending order (oldest to newest)
    });

    // Always show all grades Kindergarten through 12
    const displayGrades = ['KG', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];

    return (
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Grade</strong></TableCell>
              {sortedYears.map(year => (
                <TableCell key={year} align="center">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight="medium">
                      {year}
                    </Typography>
                    {userPermissions.canEdit && editMode && (
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteYear(year)}
                        sx={{ mt: 0.5, color: 'error.main' }}
                        title={`Delete ${year}`}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
              ))}
              <TableCell align="center"><strong>Status</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayGrades.map(grade => {

              return (
                <TableRow key={grade}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {gradeDisplayMap[grade] || grade}
                    </Typography>
                  </TableCell>
                  {sortedYears.map(year => {
                    const yearData = currentData[year] || {};
                    const gradeData = yearData[grade];
                    const value = gradeData?.total || '';
                    const gradesForYear = getGradesForYear(year);
                    const isOffered = gradesForYear.includes(grade);
                    const hasData = value !== '' && value !== 0;
                    
                    return (
                      <TableCell key={`${grade}-${year}`} align="center">
                        {editMode ? (
                          <TextField
                            size="small"
                            type="number"
                            value={value}
                            onChange={(e) => handleEnrollmentChange(year, grade, e.target.value)}
                            inputProps={{ min: 0, max: 999, style: { textAlign: 'center' } }}
                            sx={{ 
                              width: 80,
                              // Visual distinction for grades that weren't previously offered
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: !isOffered && !hasData ? '#f5f5f5' : 'white'
                              }
                            }}
                            placeholder={!isOffered ? 'Add' : ''}
                          />
                        ) : hasData || isOffered ? (
                          <Typography variant="body2">
                            {value || '--'}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            --
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell align="center">
                    {!editMode && getStatusIndicator(sortedYears[sortedYears.length - 1], grade)}
                  </TableCell>
                </TableRow>
              );
            })}
            
            {/* Total row */}
            <TableRow sx={{ borderTop: 2, borderColor: 'divider' }}>
              <TableCell>
                <Typography variant="body2" fontWeight="bold">
                  Total
                </Typography>
              </TableCell>
              {sortedYears.map(year => {
                const yearData = currentData[year] || {};
                // Calculate total from all grades that have data, not just offered grades
                const yearTotal = displayGrades.reduce((sum, grade) => {
                  return sum + (yearData[grade]?.total || 0);
                }, 0);
                
                return (
                  <TableCell key={`total-${year}`} align="center">
                    <Typography variant="body2" fontWeight="bold">
                      {yearTotal || '--'}
                    </Typography>
                  </TableCell>
                );
              })}
              <TableCell align="center">
                <Typography variant="body2" color="text.secondary">
                  --
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Render validation results
  const renderValidationResults = () => {
    if (validationResults.length === 0) return null;

    const errors = validationResults.filter(v => v.level === 'error');
    const warnings = validationResults.filter(v => v.level === 'warning');

    return (
      <Box mt={2}>
        {errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 1 }}>
            <AlertTitle>Validation Errors ({errors.length})</AlertTitle>
            {errors.map((error, index) => (
              <Typography key={index} variant="body2">
                {error.field}: {error.message}
              </Typography>
            ))}
          </Alert>
        )}
        {warnings.length > 0 && (
          <Alert severity="warning">
            <AlertTitle>Warnings ({warnings.length})</AlertTitle>
            {warnings.map((warning, index) => (
              <Typography key={index} variant="body2">
                {warning.field}: {warning.message}
              </Typography>
            ))}
          </Alert>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        <AlertTitle>Error Loading Enrollment Data</AlertTitle>
        {error}
      </Alert>
    );
  }

  return (
    <>
      {/* Header Controls */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            School: {selectedSchool?.name || 'Unknown School'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Data Years: {getAvailableYears().length} years available
          </Typography>
        </Box>
        
        {userPermissions.canEdit && (
          <Box>
            {editMode ? (
              <>
                <Button
                  variant="outlined"
                  onClick={handleCancelEdit}
                  startIcon={<CancelIcon />}
                  sx={{ mr: 1 }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleSave(false)}
                  startIcon={<SaveIcon />}
                  disabled={saveLoading}
                >
                  {saveLoading ? <CircularProgress size={20} /> : 'Save'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  onClick={handleAddYear}
                  startIcon={<AddIcon />}
                  sx={{ mr: 1 }}
                >
                  Add Year
                </Button>
                <Button
                  variant="contained"
                  onClick={handleEditMode}
                  startIcon={<EditIcon />}
                >
                  Edit Data
                </Button>
              </>
            )}
          </Box>
        )}
      </Box>

      {/* Edit Mode Help Text */}
      {editMode && (
        <Box mb={2}>
          <Typography variant="body2" color="primary">
            💡 All grades Kindergarten through 12 are shown. Gray fields indicate grades not previously offered.
            Enter enrollment numbers to add grades, or set to 0 to remove grades.
          </Typography>
        </Box>
      )}

      {/* Enrollment Grid */}
      {renderEnrollmentGrid()}

      {/* Validation Results (shown during editing) */}
      {editMode && renderValidationResults()}

      {/* Validation Dialog */}
      <Dialog open={showValidationDialog} onClose={() => setShowValidationDialog(false)} maxWidth="md">
        <DialogTitle>Validation Issues Found</DialogTitle>
        <DialogContent>
          {renderValidationResults()}
          <Typography variant="body2" sx={{ mt: 2 }}>
            You can save anyway to override these warnings, but please review the data carefully.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowValidationDialog(false)}>
            Review Data
          </Button>
          <Button onClick={handleForceSave} variant="contained" color="warning">
            Save Anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
      />
        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteConfirmDialog} onClose={handleCancelDelete} maxWidth="sm">
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Typography>
              {deleteConfirmAction?.message}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete}>
              Cancel
            </Button>
            <Button onClick={handleConfirmDelete} variant="contained" color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
    </>
  );
};

export default EnrollmentManagementModal; 